import React from 'react'
import {
  Box, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody,
  Typography, IconButton, Button, FormControlLabel, Switch
} from '@mui/material'
import { KeyboardArrowLeft, KeyboardArrowRight, Visibility } from '@mui/icons-material'
import { headingBlue, maroon, mediumGrey, lightGrey } from '../../assets/colors'
import { tableOffsets } from '../../utils/constants'
import CustomTextField from '../CustomTextField'
import CustomDropDown from '../CustomDropDown'

const outerBoxStyle = {
  width: '100%',
  marginTop: "0.2rem"
}

const paperStyle = {
  border: '1px solid #D9D9D9',
  padding: "0.2rem 0.1rem 0.1rem 0.1rem",
}

const headerBoxStyle = {
  display: 'flex',
  padding: "0.3rem",
  flexWrap: 'wrap',
}

const internalBoxStyle = {
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  width: '20%',
  flex: '1 1',
  flexDirection: "row",
  flexWrap: 'wrap'
}

const typographyStyle = {
  marginLeft: "0.5rem",
  padding: "0.1rem",
  fontSize: "1rem",
  fontWeight: "600"
}

const headerCellStyle = {
  padding: '0.2rem 0.5rem',
  fontSize: "0.9rem",
  fontWeight: "400",
  textAlign: 'center'
}

const scrollbarStyle = {
  "&::-webkit-scrollbar": { display: 'none' },
  scrollbarWidth: "none"
}

const DistrictStatsTable = ({
  displayData = [],
  totalRecs = 0,
  page = 1,
  setPage,
  offset = 10,
  setOffset,
  searchText = '',
  setSearchText,
  selectedState = 'All States',
  setSelectedState,
  stateList = [],
  includeInactive = false,
  setIncludeInactive,
  isLoading = false
}) => {
  const totalPages = Math.ceil(totalRecs / offset) || 1

  return (
    <Box style={outerBoxStyle} sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Paper style={paperStyle} sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Header Box */}
          <Box style={headerBoxStyle} sx={{ flexShrink: 0 }}>
            <Box style={internalBoxStyle}>
              <Typography style={typographyStyle}>District Wise WORKer Statistics</Typography>
            </Box>
            <Box style={{ display: 'flex', flex: "1 1", width: "80%", justifyContent: 'flex-end', padding: "0 0.5rem", flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={includeInactive}
                    onChange={(e) => setIncludeInactive(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Typography style={{ fontSize: '0.85rem', fontWeight: 600, color: headingBlue }}>
                    Include Inactive WORKers
                  </Typography>
                }
                sx={{ margin: 0, mr: 1 }}
              />
              <CustomTextField
                placeHolder="Search district, state, head..."
                value={searchText}
                setFunc={(val) => {
                  setSearchText(val)
                  setPage(1)
                }}
              />
            </Box>
          </Box>

          {/* Table Area */}
          <Box style={{ display: 'flex', justifyContent: 'center', flex: 1, overflow: 'hidden' }}>
            <TableContainer component={Paper} sx={{ width: '98%', height: "calc(100vh - 420px)", maxHeight: "calc(100vh - 420px)", border: '1px solid #D9D9D9', mt: 0.2, ...scrollbarStyle }}>
              <Table stickyHeader>
                <TableHead style={{ backgroundColor: mediumGrey }}>
                  <TableRow>
                    <TableCell style={{ ...headerCellStyle, backgroundColor: lightGrey }}>S.No</TableCell>
                    <TableCell style={{ ...headerCellStyle, backgroundColor: lightGrey }}>
                      District
                      <div style={{ fontSize: '0.78rem', fontWeight: 'normal', color: '#666666' }}>State</div>
                    </TableCell>
                    <TableCell style={{ ...headerCellStyle, backgroundColor: lightGrey }}>State Head</TableCell>
                    <TableCell style={{ ...headerCellStyle, backgroundColor: lightGrey }}>Deputy State Head</TableCell>
                    <TableCell style={{ ...headerCellStyle, backgroundColor: lightGrey }}>District Head</TableCell>
                    <TableCell style={{ ...headerCellStyle, backgroundColor: lightGrey }}>Deputy District Head</TableCell>
                    <TableCell style={{ ...headerCellStyle, backgroundColor: lightGrey }}>WORKers Count Male</TableCell>
                    <TableCell style={{ ...headerCellStyle, backgroundColor: lightGrey }}>WORKers Count Female</TableCell>
                    <TableCell style={{ ...headerCellStyle, backgroundColor: lightGrey }}>WORKers Count Total</TableCell>
                    <TableCell style={{ ...headerCellStyle, backgroundColor: lightGrey }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody style={{ textAlign: 'center' }}>
                  {displayData && displayData.length > 0 ? (
                    displayData.map((row, index) => (
                      <TableRow key={index} hover>
                        <TableCell style={headerCellStyle}>{((page - 1) * offset) + index + 1}</TableCell>
                        <TableCell style={headerCellStyle}>
                          <Typography variant="body2" style={{ fontWeight: 'bold', color: '#222222', fontSize: '0.9rem' }}>{row.district || '-'}</Typography>
                          <Typography variant="caption" color="textSecondary">{row.state || row.stateName || '-'}</Typography>
                        </TableCell>
                        <TableCell style={headerCellStyle}><Typography variant="body2">{row.stateHead || row.stateHeadMale || '-'}</Typography></TableCell>
                        <TableCell style={headerCellStyle}><Typography variant="body2">{row.deputyStateHead || row.stateHeadFemale || '-'}</Typography></TableCell>
                        <TableCell style={headerCellStyle}><Typography variant="body2">{row.districtHead || row.districtHeadMale || '-'}</Typography></TableCell>
                        <TableCell style={headerCellStyle}><Typography variant="body2">{row.deputyDistrictHead || row.districtHeadFemale || '-'}</Typography></TableCell>
                        <TableCell style={{ ...headerCellStyle, color: '#1565c0', fontWeight: 'bold' }}>
                          {includeInactive
                            ? (row.maleWorkersCountTotal ?? row.maleWorkersCount ?? 0)
                            : (row.maleWorkersCountActive ?? 0)}
                        </TableCell>
                        <TableCell style={{ ...headerCellStyle, color: '#d84315', fontWeight: 'bold' }}>
                          {includeInactive
                            ? (row.femaleWorkersCountTotal ?? row.femaleWorkersCount ?? 0)
                            : (row.femaleWorkersCountActive ?? 0)}
                        </TableCell>
                        <TableCell style={{ ...headerCellStyle, color: '#2e7d32', fontWeight: 'bold' }}>
                          {includeInactive
                            ? (row.totalWorkersCountTotal ?? row.totalWorkersCount ?? (Number(row.maleWorkersCountTotal ?? row.maleWorkersCount ?? 0) + Number(row.femaleWorkersCountTotal ?? row.femaleWorkersCount ?? 0)))
                            : (row.totalWorkersCountActive ?? (Number(row.maleWorkersCountActive ?? 0) + Number(row.femaleWorkersCountActive ?? 0)))}
                        </TableCell>
                        <TableCell style={headerCellStyle}>
                          <IconButton size="small" style={{ color: headingBlue }}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={10} style={{ ...headerCellStyle, padding: '1rem', color: maroon }}>
                        {isLoading ? 'Loading statistics...' : 'No District WORKer Statistics Found'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Pagination Controls */}
          <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: "0.5rem 0rem 0.2rem 0rem", borderTop: '1px solid #E0E0E0', marginTop: '0.5rem' }}>
            <Box style={{ width: "30%", display: 'flex', justifyContent: 'flex-start', alignItems: 'center', paddingLeft: "0.9rem" }}>
              <Typography style={{ color: maroon, fontSize: '0.9rem' }}>
                {totalRecs > 0 ? `${((page - 1) * offset) + 1} - ${Math.min(page * offset, totalRecs)} of ${totalRecs}` : '0 - 0 of 0'}
              </Typography>
            </Box>

            <Box style={{ width: '40%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <IconButton
                size='small'
                onClick={() => { if (page > 1) setPage(page - 1) }}
                disabled={page === 1}
              >
                <KeyboardArrowLeft size='small' sx={{ color: page === 1 ? mediumGrey : headingBlue }} />
              </IconButton>

              <Button variant='contained' style={{ minWidth: '0rem', width: '1.5rem', height: '1.5rem', borderRadius: '50%', backgroundColor: headingBlue, color: 'white', padding: '0rem', fontSize: '0.85rem' }}>
                {page}
              </Button>

              <IconButton
                size='small'
                onClick={() => { if (page < totalPages) setPage(page + 1) }}
                disabled={page >= totalPages}
              >
                <KeyboardArrowRight size='small' sx={{ color: page >= totalPages ? mediumGrey : headingBlue }} />
              </IconButton>
            </Box>

            <Box style={{ width: '30%', display: 'flex', justifyContent: 'flex-end', paddingRight: "0.6rem" }}>
              <CustomDropDown
                key='statsTableOffset'
                value={offset}
                width="5rem"
                setFunc={(newOffset) => {
                  setPage(1)
                  setOffset(newOffset)
                }}
                list={tableOffsets}
                regular={true}
              />
            </Box>
          </Box>
        </Paper>
      </Box>
  )
}

export default DistrictStatsTable
